import { STATUS_LABELS_EN, STATUS_LABELS_RU, type TicketStatus } from '@dreamteam/chat-protocol';

const ru = {
  title: 'Заявки',
  subtitle: 'Расскажите, что поправить или добавить — голосом, текстом, скриншотом',
  tabRequest: 'Заявка',
  tabTickets: 'Мои заявки',
  tabFeatures: 'Функции',
  placeholder: 'Что мешает или чего не хватает?',
  send: 'Отправить',
  screenshot: 'Скриншот области',
  voice: 'Записать голос',
  stop: 'Остановить запись голоса',
  file: 'Приложить файл',
  remove: 'Убрать',
  newTicket: 'Новая заявка',
  startNew: 'начать новую',
  continueAuto: 'В заявку',
  replyTo: 'Ответ по заявке',
  willCreate: 'Сообщение откроет новую заявку',
  ticket: 'Заявка',
  submitNow: 'Передать в работу',
  consent: 'Техданные',
  consentHint: 'переходы, клики, запросы к серверу — без паролей и токенов',
  record: 'Записать действия',
  recordHint: 'Нажмите и покажите, где и что не так: клики, переходы и запросы запишутся и приложатся к заявке',
  recording: 'Идёт запись действий',
  recStop: 'Остановить запись',
  recordedEvents: (n: number) => `записано: ${n} ${plural(n, 'действие', 'действия', 'действий')}`,
  events: (n: number) => `${n} ${plural(n, 'событие', 'события', 'событий')}`,
  elements: (n: number) => `${n} ${plural(n, 'элемент', 'элемента', 'элементов')}`,
  transcribing: 'Распознаём речь…',
  transcriptFailed: 'Не удалось распознать речь',
  agent: 'Команда',
  question: 'Уточняющий вопрос',
  answer: 'Ответить',
  featureReady: 'Готова функция',
  featureHint: 'Она включена только вам. Посмотрите и ответьте: подходит или нужно поправить',
  show: 'Показать',
  fits: 'Подходит',
  fix: 'Нужно поправить',
  myFeatures: 'Включены у меня',
  available: 'Можно включить',
  noFeatures: 'Пока ничего нет. Функции появятся здесь, когда по вашим заявкам что-то сделают.',
  on: 'Включить',
  off: 'Выключить',
  share: 'Поделиться с группой',
  unshare: 'Скрыть от группы',
  visibility: { me: 'видна только мне', group: 'видна моей группе', all: 'видна всем' } as Record<string, string>,
  author: 'автор',
  you: 'вы',
  daysLeft: (n: number) => `осталось дн.: ${n}`,
  users: (n: number) => `пользуются: ${n}`,
  changelog: 'Что нового',
  build: 'сборка',
  release: 'релиз',
  disabledItem: 'выключена',
  noTickets: 'Заявок пока нет. Напишите первую на вкладке «Заявка».',
  append: 'Дописать',
  empty: 'Опишите, что поправить или добавить. Можно голосом, со скриншотом и файлами — технические детали приложатся сами.',
  linked: 'Передано в GitHub',
  error: 'Не отправлено. Повторить?',
  offline: 'Нет связи с сервером',
  screenshotHint: 'Выделите область или кликните по элементу · Esc — отмена',
  close: 'Закрыть',
  open: 'Заявки и обратная связь',
  fromTicket: 'по заявке',
  loadMore: 'Показать ранние',
  fileTooBig: (name: string) => `Файл ${name} больше допустимого размера`,
  fileType: (name: string) => `Такой тип файла не принимается: ${name}`,
  status: (s: TicketStatus) => STATUS_LABELS_RU[s],
};

export type Dict = typeof ru;

const en: Dict = {
  ...ru,
  title: 'Requests',
  subtitle: 'Tell us what to fix or add — by voice, text or screenshot',
  tabRequest: 'Request',
  tabTickets: 'My requests',
  tabFeatures: 'Features',
  placeholder: 'What is wrong or what is missing?',
  send: 'Send',
  screenshot: 'Screenshot an area',
  voice: 'Record voice',
  stop: 'Stop voice recording',
  file: 'Attach a file',
  remove: 'Remove',
  newTicket: 'New request',
  startNew: 'start new',
  continueAuto: 'Into request',
  replyTo: 'Answering request',
  willCreate: 'This message opens a new request',
  ticket: 'Request',
  submitNow: 'Submit now',
  consent: 'Tech data',
  consentHint: 'navigation, clicks, API calls — no passwords or tokens',
  record: 'Record actions',
  recordHint: 'Press and show where and what is wrong: clicks, navigation and requests are recorded and attached',
  recording: 'Recording actions',
  recStop: 'Stop recording',
  recordedEvents: (n: number) => `recorded: ${n} action${n === 1 ? '' : 's'}`,
  events: (n: number) => `${n} event${n === 1 ? '' : 's'}`,
  elements: (n: number) => `${n} element${n === 1 ? '' : 's'}`,
  transcribing: 'Transcribing…',
  transcriptFailed: 'Transcription failed',
  agent: 'Team',
  question: 'Question',
  answer: 'Reply',
  featureReady: 'Feature ready',
  featureHint: 'Enabled only for you. Take a look and answer: fits or needs a fix',
  show: 'Show',
  fits: 'Fits',
  fix: 'Needs a fix',
  myFeatures: 'Enabled for me',
  available: 'Available',
  noFeatures: 'Nothing yet. Features will appear here once something is built from your requests.',
  on: 'Enable',
  off: 'Disable',
  share: 'Share with my group',
  unshare: 'Hide from group',
  visibility: { me: 'only me', group: 'my group', all: 'everyone' },
  author: 'author',
  you: 'you',
  daysLeft: (n: number) => `${n} days left`,
  users: (n: number) => `${n} using`,
  changelog: "What's new",
  build: 'build',
  release: 'release',
  disabledItem: 'disabled',
  noTickets: 'No requests yet. Write the first one on the “Request” tab.',
  append: 'Add more',
  empty: 'Describe what to fix or add. Voice, screenshots and files are welcome — technical details are attached automatically.',
  linked: 'Sent to GitHub',
  error: 'Not sent. Retry?',
  offline: 'No connection',
  screenshotHint: 'Drag to select an area or click an element · Esc to cancel',
  close: 'Close',
  open: 'Requests and feedback',
  fromTicket: 'from request',
  loadMore: 'Show earlier',
  fileTooBig: (name: string) => `File ${name} is too large`,
  fileType: (name: string) => `File type not accepted: ${name}`,
  status: (s: TicketStatus) => STATUS_LABELS_EN[s],
};

function plural(n: number, one: string, few: string, many: string) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export function dict(locale: 'ru' | 'en' = 'ru'): Dict {
  return locale === 'en' ? en : ru;
}
